import { Injectable } from '@angular/core';
import { Store } from '@ngrx/store';
import { map } from 'rxjs/operators';

import { selectCurrentUser } from 'app/_state/auth/auth-selectors';
import { AuthState } from 'app/_state/auth/auth-state';

import { NoteDetailsFragment, UserRole } from 'generated/graphql';

@Injectable({
  providedIn: 'root'
})
export class NotePermissionsService {

  private _currentUser$ = this.store.select(
    selectCurrentUser
  );

  /**
   * Helper for determining is a user can remove or update a note.
   */
  constructor(
    private store: Store<AuthState>
  ) {
  }

  // TODO: Note should contain the targetType eventually so we don't have to pass it in separately here
  canPinNote(note: NoteDetailsFragment, noteTargetType: string) {
    return this._currentUser$.pipe(
      map(user => {
        if (noteTargetType === 'TaggableLogbook') {
          return false;
        }

        if (noteTargetType === 'Campaign') {
          return user?.role === UserRole.Administrator || user?.role === UserRole.Lead;
        }

        return user?.role != UserRole.Activist;
      })
    );
  }

  canEditNote(note: NoteDetailsFragment, noteCampaignId?: string, currentCampaignId?: string) {
    // Cannot edit note outside of note campaign context
    return this._currentUser$.pipe(
      map(user => {
        if (!this.noteIsInCampaign(noteCampaignId, currentCampaignId)) {
          return false;
        }

        // Everyone can only edit their own notes.
        return this.isNoteOwner(note, user?.id);
      })
    );
  }

  canRemoveNote(note: NoteDetailsFragment, noteCampaignId?: string, currentCampaignId?: string) {
    // Cannot remove note outside of note campaign context

    return this._currentUser$.pipe(
      map(user => {
        if (!this.noteIsInCampaign(noteCampaignId, currentCampaignId)) {
          return false;
        }

        // Admins can remove any notes
        if (user?.role === UserRole.Administrator) {
          return true;
        }

        // Everyone else can only remove their own notes.
        return this.isNoteOwner(note, user?.id);
      })
    );
  }

  /**
   * Returns true if note was created in the current campaign
   * if noteCampaignId is null it also returns true
   *
   * @param noteCampaignId
   */
  private noteIsInCampaign(noteCampaignId?: string, currentCampaignId?: string) {
    if (!noteCampaignId) {
      return true;
    }

    return currentCampaignId === noteCampaignId;
  }

  private isNoteOwner(note: NoteDetailsFragment, userId: string) {
    return note?.createdBy?.id?.toString() === userId?.toString();
  }
}
